import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Heart, CheckCircle, Clock, Trash2 } from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PrayerRequestsPage({ user }) {
  const [prayers, setPrayers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: ""
  });

  const isSuperAdmin = user.role === 'super_admin';

  useEffect(() => {
    fetchPrayers();
  }, []);

  const fetchPrayers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/prayer-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrayers(response.data);
    } catch (error) {
      toast.error("Failed to fetch prayer requests");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/prayer-requests`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Prayer request submitted successfully");
      fetchPrayers();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to submit prayer request");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (prayerId, status) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/prayer-requests/${prayerId}?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status updated successfully");
      fetchPrayers();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deletePrayer = async (prayerId) => {
    if (!window.confirm("Are you sure you want to delete this answered prayer request?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/prayer-requests/${prayerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Prayer request deleted successfully");
      fetchPrayers();
    } catch (error) {
      toast.error("Failed to delete prayer request");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: ""
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'answered':
        return 'bg-green-100 text-green-700';
      case 'ongoing':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'answered':
        return <CheckCircle className="h-4 w-4" />;
      case 'ongoing':
        return <Clock className="h-4 w-4" />;
      default:
        return <Heart className="h-4 w-4" />;
    }
  };

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          <span className="gradient-text">Prayer Requests</span>
        </h1>
        <p className="text-gray-600">
          {isSuperAdmin 
            ? "Submit and manage all prayer requests" 
            : "Submit and view your prayer requests"}
        </p>
      </div>

      <div className="stat-card mb-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/80 text-sm">Total Prayer Requests</p>
            <p className="text-3xl font-bold" data-testid="total-prayers">{prayers.length}</p>
          </div>
          <Heart className="h-12 w-12 text-white/40" />
        </div>
      </div>

      <div className="card mb-6">
        <div className="flex justify-end">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-prayer-button" className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700">
                <Plus className="mr-2 h-4 w-4" /> Submit Prayer Request
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>Submit Prayer Request</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    data-testid="prayer-title-input"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Prayer for healing"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    data-testid="prayer-description-input"
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Please describe your prayer request..."
                    rows={5}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button data-testid="submit-prayer-button" type="submit" disabled={loading} className="bg-gradient-to-r from-purple-600 to-indigo-600">
                    {loading ? "Submitting..." : "Submit Request"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-4">
        {prayers.map((prayer) => (
          <div key={prayer.id} className="card" data-testid={`prayer-card-${prayer.id}`}>
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 flex items-center justify-center flex-shrink-0">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{prayer.title}</h3>
                    <p className="text-sm text-gray-500">
                      By {prayer.requester_name} • {format(new Date(prayer.created_at), "MMM d, yyyy")}
                    </p>
                  </div>
                  <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(prayer.status)}`}>
                    {getStatusIcon(prayer.status)}
                    <span className="capitalize">{prayer.status}</span>
                  </div>
                </div>
                <p className="text-gray-700 mb-3">{prayer.description}</p>
                
                {isSuperAdmin && (
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(prayer.id, 'ongoing')}
                      disabled={prayer.status === 'ongoing'}
                    >
                      Mark Ongoing
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateStatus(prayer.id, 'answered')}
                      disabled={prayer.status === 'answered'}
                      className="text-green-600 hover:text-green-700"
                    >
                      Mark Answered
                    </Button>
                    {prayer.status === 'answered' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deletePrayer(prayer.id)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-prayer-${prayer.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {prayers.length === 0 && (
        <div className="text-center py-12">
          <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No prayer requests yet. Submit your first request!</p>
        </div>
      )}
    </div>
  );
}
